import { Button, Card, Space, Tabs, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { File as EntityFile, Page, ShipWaterGauge } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    Image as ZhImage,
    MultiGrid as ZhMultiGrid
} from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { fileService } from "@/api/services/fileService";
import { DEFAULT_VALUE } from "@/config";
import { operationHistoryService } from "@/api/services/operationHistoryService";
import type { OperationHistory } from "@/entity/logistics/operationHistory";
import { Operation_History_Enum } from "@/meta/logistics/operationHistory";
import { AIS_SHIP_TYPE, RECOGNIZE_RESULT, SHIP_INFO_STATE } from "@/meta/logistics/shipInfo";
import { BULK_CARGO_SHIP_TYPE } from "@zhihaoscm/meta";
import { useUserStore } from "@/stores/react/authStore";
import type { ShipInfoRow } from "./ShipInfoPage";
import ShipPortraitPanel, {
    loadShipPortrait,
    type ShipPortReportQuery,
    type ShipPortReportPage
} from "./ShipPortraitPanel";
import ShipDeviceBindingModal, {
    bindShipDevice,
    loadShipDeviceOptions,
    type ShipDevice,
    type ShipDeviceBindingPayload
} from "./ShipDeviceBindingModal";
import ShipMonitorModal, {
    loadShipMonitorStream,
    loadShipPlaybackStream,
    stopShipMonitorStream,
    stopShipPlaybackStream,
    type ShipMonitorDevice,
    type ShipMonitorStream
} from "./ShipMonitorModal";
import { ShipWaterGaugePanel, type ShipWaterGaugeQuery } from "./ShipWaterGaugePanel";

type ShipMediaFile = Pick<EntityFile, "id" | "path">;
type ShipCertificateFiles = Partial<NonNullable<ShipInfoRow["ship"]["certificateFiles"]>>;
type ShipMediaKey =
    | "shipPhotos"
    | "peopleShipPhotos"
    | "videos"
    | "captainFront"
    | "captainBack"
    | "shipBusiness"
    | "shipBusinessTonnage"
    | "shipAis"
    | "shipNationality"
    | "shipInspection"
    | "shipMinSafe"
    | "shipSafeEnvironment"
    | "competency"
    | "vesselCalling";
type ShipMediaState = Record<ShipMediaKey, ShipMediaFile[]>;

const emptyShipMedia = (): ShipMediaState => ({
    shipPhotos: [],
    peopleShipPhotos: [],
    videos: [],
    captainFront: [],
    captainBack: [],
    shipBusiness: [],
    shipBusinessTonnage: [],
    shipAis: [],
    shipNationality: [],
    shipInspection: [],
    shipMinSafe: [],
    shipSafeEnvironment: [],
    competency: [],
    vesselCalling: []
});

/** 复用旧 Vue 详情的字段集合，只从当前 `/ship/vo/:id` 返回的文件 ID 读取媒体。 */
function getShipMediaIdGroups(ship: ShipInfoRow["ship"]): Record<ShipMediaKey, number[]> {
    const certificateFiles = (ship.certificateFiles || {}) as ShipCertificateFiles;
    // 旧 Vue 以 truthy 判断单文件证书，0 是后端的空值占位，不能提交给文件接口。
    const one = (id?: number) => (id ? [id] : []);
    return {
        shipPhotos: ship.shipPhotoFiles || [],
        peopleShipPhotos: ship.peopleShipGroupPhotoFiles || [],
        // 保持旧 Vue PIC_LIST 的图片组顺序，视频在图片闭集之后单独归组。
        competency: certificateFiles.competencyCertificateFile || [],
        shipBusiness: one(certificateFiles.shipBusinessTransportCertificateFile),
        shipBusinessTonnage: one(certificateFiles.shipBusinessTonnagCertificateFile),
        captainFront: one(certificateFiles.captainsIdCardFrontFile),
        shipMinSafe: one(certificateFiles.shipMinSafetyManningCertificateFile),
        shipInspection: one(certificateFiles.shipInspectionReportFile),
        shipNationality: one(certificateFiles.shipNationalityCertificateFile),
        shipAis: one(certificateFiles.shipAutomaticIdentifyCertificateFile),
        captainBack: one(certificateFiles.captainsIdCardBackFile),
        shipSafeEnvironment: one(certificateFiles.shipSafetyEnvironmentalProtectionCertificateFile),
        vesselCalling: one(certificateFiles.vesselCallingAgreementFile),
        videos: ship.shipVideoFiles || []
    };
}

/** 复用现有 dataService 文件批量接口，不改变船舶详情接口契约。 */
export async function loadShipInfoFiles(ids: number[]): Promise<ShipMediaFile[]> {
    return fileService.getFileByIds(ids);
}

function collectShipMedia(files: ShipMediaFile[], groups: Record<ShipMediaKey, number[]>): ShipMediaState {
    const filesById = new Map(files.map(file => [String(file.id), file]));
    return Object.fromEntries(
        Object.entries(groups).map(([key, ids]) => [
            key,
            ids.map(id => filesById.get(String(id))).filter((file): file is ShipMediaFile => Boolean(file?.path))
        ])
    ) as ShipMediaState;
}

function ShipMediaPlaceholder({ type, variant }: { type: "image" | "video"; variant: "ship" | "certificate" }) {
    const width = type === "video" ? 248 : variant === "ship" ? 160 : 240;
    const height = type === "video" ? 140 : variant === "ship" ? 90 : 135;
    return (
        <div className="flex items-center justify-center bg-[#f5f5f5]" style={{ height, width }}>
            {type === "video" ? "暂无视频" : "暂无图片"}
        </div>
    );
}

function ShipMediaValue({
    label,
    files,
    type,
    variant
}: {
    label: string;
    files: ShipMediaFile[];
    type: "image" | "video";
    variant: "ship" | "certificate";
}) {
    if (!files.length) return <ShipMediaPlaceholder type={type} variant={variant} />;
    if (type === "video") {
        return (
            <div className="flex flex-wrap gap-2">
                {files.map(file => (
                    <video key={String(file.id)} aria-label={label} controls height={138} src={file.path} width={246} />
                ))}
            </div>
        );
    }
    const previewSrcList = files.map(file => file.path);
    return (
        <div className="flex flex-wrap gap-2">
            {files.map((file, index) => (
                <ZhImage
                    key={String(file.id)}
                    alt={label}
                    fit="contain"
                    height={variant === "ship" ? 90 : 135}
                    initialIndex={index}
                    preview
                    previewSrcList={previewSrcList}
                    src={file.path}
                    width={variant === "ship" ? 160 : 240}
                />
            ))}
        </div>
    );
}

function ShipMediaItem({
    label,
    files,
    type = "image",
    variant = "certificate",
    span
}: {
    label: string;
    files: ShipMediaFile[];
    type?: "image" | "video";
    variant?: "ship" | "certificate";
    span?: number;
}) {
    return (
        <ZhBaseItem
            className="direction-column"
            label={label}
            span={span}
            value={<ShipMediaValue files={files} label={label} type={type} variant={variant} />}
        />
    );
}

export interface ShipInfoDetailPageProps {
    loadShip?: (id: string) => Promise<ShipInfoRow>;
    loadShipFiles?: (ids: number[]) => Promise<ShipMediaFile[]>;
    loadWaterGauge?: (query: ShipWaterGaugeQuery) => Promise<Page<ShipWaterGauge>>;
    loadPortrait?: typeof loadShipPortrait;
    loadPortReports?: (shipId: string, params: ShipPortReportQuery) => Promise<ShipPortReportPage>;
    loadDevices?: (id: string) => Promise<ShipDevice[]>;
    loadDeviceOptions?: (keyword: string) => Promise<ShipDevice[]>;
    bindDevice?: (payload: ShipDeviceBindingPayload) => Promise<unknown>;
    loadMonitorStream?: (device: ShipMonitorDevice) => Promise<ShipMonitorStream | undefined>;
    loadPlaybackStream?: (device: ShipMonitorDevice, playbackAt?: string) => Promise<ShipMonitorStream | undefined>;
    stopMonitorStream?: (serialNo: string, liveId: string) => Promise<unknown>;
    stopPlaybackStream?: (serialNo: string, liveId: string) => Promise<unknown>;
    prepareMonitor?: (id: string) => Promise<unknown>;
    unbindShip?: (id: string) => Promise<unknown>;
    loadOperationHistory?: (bizNo: string) => Promise<OperationHistory[]>;
    permissions?: string[];
    banButton?: boolean;
}

/** 复用 Vue 船舶详情接口，保留物流域 `/ship/vo/:id` 契约。 */
export async function loadShipInfoDetail(id: string): Promise<ShipInfoRow> {
    const response = await shippingRequestHttp.service.get<ShipInfoRow>(`/ship/vo/${encodeURIComponent(id)}`);
    return response.data;
}

/** 复用 Vue 设备按钮的船舶设备查询契约。 */
export async function loadShipInfoDevices(id: string): Promise<ShipDevice[]> {
    const response = await shippingRequestHttp.service.get<ShipInfoRow["devices"]>(
        `/ship/find/devices/${encodeURIComponent(id)}`
    );
    return (response.data || []) as ShipDevice[];
}

/** 复用 Vue 监控按钮在缺少定位时的船舶补全契约。 */
export async function prepareShipInfoMonitor(id: string) {
    const response = await shippingRequestHttp.service.get(`/ship/find-create/${encodeURIComponent(id)}`);
    return response.data;
}

/** 复用 Vue 解绑按钮的船舶解绑契约。 */
export async function unbindShipInfo(id: string) {
    const response = await shippingRequestHttp.service.put(`/ship/unbind/${encodeURIComponent(id)}`);
    return response.data;
}

/** 复用旧 Vue 船舶详情的操作历史接口和船舶信息模块编号。 */
export async function loadShipInfoOperationHistory(bizNo: string): Promise<OperationHistory[]> {
    return operationHistoryService.getDetail({
        bizNo,
        module: Operation_History_Enum.LOGISTICS_MANAGEMENT_SHIP_MANAGEMENT_I
    });
}

/** 暴露画像默认请求，便于详情页外部复用和测试注入契约。 */
export { loadShipPortrait };

function text(value: unknown): string {
    return value == null || value === "" ? DEFAULT_VALUE : String(value);
}

function dateText(value: unknown): string {
    if (typeof value !== "string" || !value) return DEFAULT_VALUE;
    return value.replace("T", " ").slice(0, 16);
}

function enumText(meta: { getIntroByCode: (code?: number) => string }, value?: number): string {
    return meta.getIntroByCode(value) || DEFAULT_VALUE;
}

/** 展示旧 Vue OperationHistoryItem 的动作、时间和可展开字段变更。 */
function ShipOperationHistory({ logs }: { logs: OperationHistory[] }) {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    if (!logs.length) return <Typography.Text type="secondary">暂无操作记录</Typography.Text>;

    return (
        <ol aria-label="船舶操作记录" className="m-0 list-none p-0">
            {logs.map((log, index) => {
                const key = String(log.id || index);
                const changes = Array.isArray(log.operationContent) ? log.operationContent : [];
                const isExpanded = Boolean(expanded[key]);
                return (
                    <li key={key} className="mb-3">
                        <div className="flex items-start gap-2">
                            <span>{index + 1}.</span>
                            <span>{dateText(log.createdTime)}</span>
                            <span>{`${log.operationAction || DEFAULT_VALUE}。`}</span>
                            {changes.length ? (
                                <Button
                                    aria-label={isExpanded ? "收起操作详情" : "展开操作详情"}
                                    size="small"
                                    type="link"
                                    onClick={() => setExpanded(current => ({ ...current, [key]: !isExpanded }))}
                                >
                                    {isExpanded ? "收起" : "详情"}
                                </Button>
                            ) : null}
                        </div>
                        {isExpanded ? (
                            <div className="ml-6 mt-2">
                                {changes.map((change, changeIndex) => (
                                    <div key={`${key}-${changeIndex}`}>
                                        修改了 <strong>{change.key || DEFAULT_VALUE}</strong>，旧值为“
                                        {change.oldValue || DEFAULT_VALUE}”，新值为“{change.newValue || DEFAULT_VALUE}
                                        ”。
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </li>
                );
            })}
        </ol>
    );
}

const shipInfoDetailTabsStorageKey = "shipInfoDetailTabs";

function getInitialShipInfoTab(): string {
    if (typeof window === "undefined") return "info";
    const cachedTab = window.localStorage.getItem(shipInfoDetailTabsStorageKey);
    return cachedTab === "waterGauge" || cachedTab === "portrait" ? cachedTab : "info";
}

/** 物流管理-船舶信息详情，完整复用 Vue 详情字段、媒体区和状态页签。 */
export function ShipInfoDetailPage({
    loadShip = loadShipInfoDetail,
    loadShipFiles = loadShipInfoFiles,
    loadWaterGauge,
    loadPortrait,
    loadPortReports: loadPortReportsFetcher,
    loadDevices = loadShipInfoDevices,
    loadDeviceOptions = loadShipDeviceOptions,
    bindDevice = bindShipDevice,
    loadMonitorStream = loadShipMonitorStream,
    loadPlaybackStream = loadShipPlaybackStream,
    stopMonitorStream = stopShipMonitorStream,
    stopPlaybackStream = stopShipPlaybackStream,
    prepareMonitor = prepareShipInfoMonitor,
    unbindShip = unbindShipInfo,
    loadOperationHistory = loadShipInfoOperationHistory,
    permissions,
    banButton
}: ShipInfoDetailPageProps) {
    const { id = "" } = useParams();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeBanButton = useUserStore(state => state.banButton);
    const isButtonBanned = banButton ?? storeBanButton;
    // 对齐旧 Vue v-auth；未提供权限上下文时保留嵌入测试与历史调用方的兼容行为。
    const hasPermissionContext =
        permissions !== undefined || storePermissions.some(permission => permission.startsWith("ROLE_SHIP"));
    const can = (permission: string) => !hasPermissionContext || (permissions ?? storePermissions).includes(permission);
    const [detail, setDetail] = useState<ShipInfoRow | null>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [activeTab, setActiveTab] = useState(getInitialShipInfoTab);
    const [deviceModalOpen, setDeviceModalOpen] = useState(false);
    const [deviceDialogDevices, setDeviceDialogDevices] = useState<ShipDevice[]>([]);
    const [monitorOpen, setMonitorOpen] = useState(false);
    const [monitorDevice, setMonitorDevice] = useState<ShipMonitorDevice>();
    const [shipMedia, setShipMedia] = useState<ShipMediaState>(emptyShipMedia);
    const [operationHistory, setOperationHistory] = useState<OperationHistory[]>([]);

    /** 沿用 Vue shipInfoDetailTabs 缓存，保留用户离开详情前选中的页签。 */
    useEffect(() => {
        window.localStorage.setItem(shipInfoDetailTabsStorageKey, activeTab);
    }, [activeTab]);

    useEffect(() => {
        if (!id) {
            setErrorMessage("缺少船舶编号");
            return;
        }
        let disposed = false;
        void loadShip(id)
            .then(async result => {
                if (disposed) return;
                // 先按旧 Vue 文件列表解析媒体，避免详情区先展示空占位再突然补齐真实文件。
                const groups = getShipMediaIdGroups(result.ship);
                const ids = [...new Set(Object.values(groups).flat())];
                if (!ids.length) {
                    setShipMedia(emptyShipMedia());
                    setDetail(result);
                    return;
                }
                try {
                    const files = await loadShipFiles(ids);
                    if (disposed) return;
                    setShipMedia(collectShipMedia(files, groups));
                } catch {
                    if (disposed) return;
                    setShipMedia(emptyShipMedia());
                }
                setDetail(result);
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) setErrorMessage("船舶详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadShip, loadShipFiles]);

    useEffect(() => {
        if (!id) {
            setOperationHistory([]);
            return;
        }
        let disposed = false;
        setOperationHistory([]);
        // 旧 Vue 以路由中的 MMSI/船舶编号作为 bizNo 查询船舶信息操作历史。
        void loadOperationHistory(id)
            .then(result => {
                if (!disposed) setOperationHistory(result || []);
            })
            .catch(() => {
                if (!disposed) setOperationHistory([]);
            });
        return () => {
            disposed = true;
        };
    }, [id, loadOperationHistory]);

    const ship = detail?.ship;
    const certified = Number(ship?.state) === SHIP_INFO_STATE.CERTIFIED.code;
    const warehouseState = Number(ship?.warehouseState);
    const showRemainingLayTime =
        (warehouseState === RECOGNIZE_RESULT.LOADING.code || warehouseState === RECOGNIZE_RESULT.UNLOADING.code) &&
        ship?.remainingLayTime != null;

    /** 跳转到旧 Vue 详情使用的 FindMmsi 船舶查询入口。 */
    const handleFindShip = () => {
        if (id) navigate(`/logistics/findLocation/${encodeURIComponent(id)}`);
    };

    /** 跳转到旧 Vue 已注册的绑定页面，不臆造绑定接口。 */
    const handleBind = () => {
        if (id) navigate(`/logistics/shipManage/shipInfo/shipInfoBind/${encodeURIComponent(id)}/1`);
    };

    /** 查询当前设备后打开 Vue 对应的船舶设备管理弹窗。 */
    const handleDevice = async () => {
        if (!id) return;
        try {
            const devices = await loadDevices(id);
            setDeviceDialogDevices(devices || []);
            setDetail(current => (current ? { ...current, devices } : current));
            setDeviceModalOpen(true);
        } catch {
            message.error("设备查询失败");
        }
    };

    /** 设备绑定或解绑成功后重新加载详情，确保页面状态与后端一致。 */
    const handleDeviceSuccess = async (payload: ShipDeviceBindingPayload) => {
        if (!id) return;
        setDeviceModalOpen(false);
        try {
            const refreshed = await loadShip(id);
            setDetail(refreshed);
            message.success(payload.flag === 1 ? "绑定成功" : "解绑成功");
        } catch {
            message.error("设备状态刷新失败");
        }
    };

    /** 按 Vue 逻辑补全定位数据后打开监控调阅弹窗。 */
    const handleMonitor = async () => {
        if (!id) return;
        try {
            await prepareMonitor(id);
            const device = detail?.devices?.[0] as ShipMonitorDevice | undefined;
            setMonitorDevice(device);
            setMonitorOpen(true);
        } catch {
            message.error("监控加载失败");
        }
    };

    /** 解绑成功后重新查询详情，失败时仅显示错误反馈。 */
    const handleUnbind = async () => {
        if (!id) return;
        try {
            await unbindShip(id);
            const refreshed = await loadShip(id);
            setDetail(refreshed);
            message.success("解绑成功");
        } catch {
            message.error("解绑失败");
        }
    };

    const infoContent = detail ? (
        <ZhMultiGrid className="w-full">
            <div data-testid="ship-detail-media-grid">
                <section className="mb-6">
                    <div className="mb-4 text-base font-medium">基本信息</div>
                    <ZhGrid cols={4} gap={[16, 20]}>
                        <ZhBaseItem label="中文船名" value={text(ship?.cnname)} />
                        <ZhBaseItem label="英文船名" value={text(ship?.name)} />
                        <ZhBaseItem label="类型" value={enumText(AIS_SHIP_TYPE, ship?.type)} />
                        {Number(ship?.type) === AIS_SHIP_TYPE.CARGO_SHIPS.code ? (
                            <ZhBaseItem
                                label="散货船细分"
                                value={BULK_CARGO_SHIP_TYPE.getIntroByCode(ship?.bulkCargoShipType) || DEFAULT_VALUE}
                            />
                        ) : null}
                        <ZhBaseItem label="MMSI" value={text(ship?.id)} />
                        <ZhBaseItem label="IMO" value={text(ship?.imo)} />
                        <ZhBaseItem label="呼号" span={2} value={text(ship?.callSign)} />
                        <ZhBaseItem
                            label="船长/宽"
                            value={`${ship?.length ? `${ship.length}米` : DEFAULT_VALUE} / ${ship?.width ? `${ship.width}米` : DEFAULT_VALUE}`}
                        />
                        <ZhBaseItem label="吃水" value={ship?.draft ? `${ship.draft}米` : DEFAULT_VALUE} />
                        <ZhBaseItem label="型深" value={ship?.deep ? `${ship.deep}米` : DEFAULT_VALUE} />
                    </ZhGrid>
                </section>
                <section className="mb-6">
                    <div className="mb-4 text-base font-medium">档案信息</div>
                    <ZhGrid cols={4} gap={[16, 20]}>
                        <ZhBaseItem label="总吨" value={text(ship?.tonTotal)} />
                        <ZhBaseItem label="载重量" value={text(ship?.tonCapacity)} />
                        <ZhBaseItem label="净吨" span={2} value={text(ship?.tonPure)} />
                        <ZhBaseItem label="资料完善进度" value={`${text(ship?.dataProgress)}%`} />
                        {certified ? (
                            <>
                                <ZhBaseItem
                                    label="营运证截止时间"
                                    value={dateText(ship?.operatingLicenseExpirationDate)}
                                />
                                <ZhBaseItem label="船舶经营人名称" value={text(ship?.shipOperatorName)} />
                                <ZhBaseItem label="国籍证截止时间" value={dateText(ship?.nationalityExpirationDate)} />
                                <ZhBaseItem
                                    label="船检证截止时间"
                                    value={dateText(ship?.shipInspectionExpirationDate)}
                                />
                                <ZhBaseItem label="船舶营运证号" value={text(ship?.operatingLicenseNumber)} />
                                <ZhBaseItem label="建造日期" value={dateText(ship?.constructionDate)} />
                                <ZhBaseItem label="船籍港" value={text(ship?.homePortCityCode)} />
                                <ZhBaseItem label="适任职务资格" value={text(ship?.jobQualification)} />
                            </>
                        ) : null}
                    </ZhGrid>
                    <ZhGrid cols={4} gap={[16, 20]}>
                        <ShipMediaItem label="船只照片" files={shipMedia.shipPhotos} variant="ship" />
                        <ShipMediaItem label="人船合照" files={shipMedia.peopleShipPhotos} variant="ship" />
                        <ShipMediaItem label="船只视频" files={shipMedia.videos} type="video" variant="ship" />
                    </ZhGrid>
                </section>
                {detail.devices?.length ? (
                    <section className="mb-6">
                        <div className="mb-4 text-base font-medium">设备信息</div>
                        <ZhGrid cols={4} gap={[16, 20]}>
                            {detail.devices.map(device => (
                                <ZhBaseItem key={String(device.id)} label="设备名称" value={text(device.name)} />
                            ))}
                        </ZhGrid>
                    </section>
                ) : null}
                <section>
                    <div className="mb-4 text-base font-medium">账号信息</div>
                    <ZhGrid cols={4} gap={[16, 20]}>
                        {certified ? (
                            <>
                                <ZhBaseItem label="组织机构名" value={text(detail.customer?.institutionName)} />
                                <ZhBaseItem label="个人实名" value={text(detail.customer?.realName)} />
                                <ZhBaseItem label="用户ID" span={2} value={text(detail.customer?.code)} />
                            </>
                        ) : null}
                        <ZhBaseItem label="所有人(船主)" value={text(ship?.captain)} />
                        <ZhBaseItem label="联系方式(船主)" value={text(ship?.mobile)} />
                        <ZhBaseItem label="联系方式维护人" value={text(ship?.contactMaintainer)} />
                    </ZhGrid>
                </section>
                {certified ? (
                    <>
                        <section className="mb-6">
                            <div className="mb-4 text-base font-medium">身份证信息</div>
                            <ZhGrid cols={4} gap={[16, 20]}>
                                <ShipMediaItem label="船长身份证正面(国徽面)" files={shipMedia.captainFront} />
                                <ShipMediaItem label="船长身份证反面(人像面)" files={shipMedia.captainBack} />
                            </ZhGrid>
                        </section>
                        <section className="mb-6">
                            <div className="mb-4 text-base font-medium">船运资质</div>
                            <ZhGrid cols={4} gap={[16, 20]}>
                                <ShipMediaItem label="船只营业运输证" files={shipMedia.shipBusiness} />
                                <ShipMediaItem label="船舶营运证吨位页" files={shipMedia.shipBusinessTonnage} />
                                <ShipMediaItem label="船舶自动识别系统AIS标识码证书" files={shipMedia.shipAis} />
                                <ShipMediaItem label="船舶国籍证书" files={shipMedia.shipNationality} />
                                <ShipMediaItem label="内河船舶检验报告" files={shipMedia.shipInspection} />
                                <ShipMediaItem label="内河船舶最低安全配员证书" files={shipMedia.shipMinSafe} />
                                <ShipMediaItem
                                    label="内河船舶安全与环保证书(大簿子首页)"
                                    files={shipMedia.shipSafeEnvironment}
                                />
                                <ShipMediaItem label="船员适任证书" files={shipMedia.competency} span={4} />
                                <ShipMediaItem label="船只挂靠协议/租赁协议" files={shipMedia.vesselCalling} />
                            </ZhGrid>
                        </section>
                    </>
                ) : null}
                {certified ? <ZhBaseItem label="备注" value={text(ship?.remark)} /> : null}
            </div>
            <Card className="mt-2" size="small" title="历史记录">
                <ShipOperationHistory logs={operationHistory} />
            </Card>
        </ZhMultiGrid>
    ) : null;

    return (
        <section aria-label="ship-info-detail-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <ZhDetailHeader
                    leftInfos={[
                        { label: "创建时间", value: dateText(ship?.createdTime) },
                        {
                            label: "货仓状态",
                            // 详情头配置沿用旧 Vue 的纯文本值，避免 zhihao-ui-react 兼容层将 React 节点转成 [object Object]。
                            value: `${enumText(RECOGNIZE_RESULT, ship?.warehouseState)}${
                                showRemainingLayTime ? ` (预计剩余${ship?.remainingLayTime}h)` : ""
                            }`
                        }
                    ]}
                    stateDesc={enumText(SHIP_INFO_STATE, ship?.state)}
                    title="船舶信息详情"
                />
            </Card>
            <Card>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {detail ? (
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={[
                            { key: "info", label: "船舶信息", children: infoContent },
                            {
                                key: "waterGauge",
                                label: "水尺表",
                                children:
                                    activeTab === "waterGauge" && ship?.id != null ? (
                                        <ShipWaterGaugePanel
                                            shipId={String(ship.id)}
                                            shipDeep={ship.deep == null ? undefined : Number(ship.deep)}
                                            loadWaterGauge={loadWaterGauge}
                                            onBulkUpload={waterGaugeShipId =>
                                                navigate(
                                                    `/logistics/shipManage/shipInfo/waterGaugeBulkUploads/${encodeURIComponent(waterGaugeShipId)}`
                                                )
                                            }
                                        />
                                    ) : null
                            },
                            {
                                key: "portrait",
                                label: "船舶画像",
                                children:
                                    activeTab === "portrait" && ship?.id != null ? (
                                        <ShipPortraitPanel
                                            shipId={String(ship.id)}
                                            shipState={ship.state}
                                            loadPortrait={loadPortrait}
                                            loadPortReports={loadPortReportsFetcher}
                                        />
                                    ) : null
                            }
                        ]}
                    />
                ) : null}
            </Card>
            <Space className="operation-fixed-btn" size={0} wrap>
                {can("ROLE_LF_SHIP") ? (
                    <Button aria-label="查询该船舶" disabled={isButtonBanned} type="primary" onClick={handleFindShip}>
                        查询该船舶
                    </Button>
                ) : null}
                {can("ROLE_SHIP_W") ? (
                    <Button
                        aria-label="设备"
                        disabled={isButtonBanned}
                        type="primary"
                        onClick={() => void handleDevice()}
                    >
                        设备
                    </Button>
                ) : null}
                {can("ROLE_SHIP_W") ? (
                    <Button
                        aria-label="修改"
                        disabled={isButtonBanned}
                        type="primary"
                        onClick={() =>
                            navigate(`/logistics/shipManage/shipInfo/shipInfoEdit/${encodeURIComponent(id)}`)
                        }
                    >
                        修改
                    </Button>
                ) : null}
                {detail?.devices?.length && can("ROLE_SHIP_R") ? (
                    <Button
                        aria-label="监控"
                        disabled={isButtonBanned}
                        type="primary"
                        onClick={() => void handleMonitor()}
                    >
                        监控
                    </Button>
                ) : null}
                {can("ROLE_SHIP_W") ? (
                    <Button
                        aria-label={certified ? "解绑" : "绑定"}
                        disabled={isButtonBanned}
                        type="primary"
                        onClick={certified ? () => void handleUnbind() : handleBind}
                    >
                        {certified ? "解绑" : "绑定"}
                    </Button>
                ) : null}
                <Button aria-label="返回" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </Space>
            {id ? (
                <ShipDeviceBindingModal
                    open={deviceModalOpen}
                    shipId={id}
                    devices={deviceDialogDevices}
                    loadDeviceOptions={loadDeviceOptions}
                    bindDevice={bindDevice}
                    onSuccess={handleDeviceSuccess}
                    onCancel={() => setDeviceModalOpen(false)}
                />
            ) : null}
            <ShipMonitorModal
                open={monitorOpen}
                device={monitorDevice}
                loadMonitorStream={loadMonitorStream}
                loadPlaybackStream={loadPlaybackStream}
                stopMonitorStream={stopMonitorStream}
                stopPlaybackStream={stopPlaybackStream}
                shipId={ship?.id == null ? id : String(ship.id)}
                ship={ship}
                onCancel={() => setMonitorOpen(false)}
            />
        </section>
    );
}

export default ShipInfoDetailPage;
